/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import Grid from '@material-ui/core/Grid';
import {makeStyles} from '@material-ui/core/styles';
import Chip, {ChipVariant} from '@ion/dls/src/components/chip/Chip';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';

export interface AssociatedDevicesGridItemProps {
  labels: {
    associatedDevicesTitle?: string;
    tags?: string[];
  };
  tags?: string[];
  associatedTitle?: string;
  associatedLabel?: string;
  associatedValue?: string;
}

const useStyles = makeStyles(() => ({
  formItem : {
    padding : '0px 16px'
  },
  multiItemBody : {
    ...dlsAlt('typography', 'label2'),
    color         : dlsColor('grey1'),
    padding       : '16px 0px 8px',
    fontSize      : '16px',
    minHeight     : '1px',
    letterSpacing : 0
  },
  tagChipContainer : {
    '& > *' : {
      margin : '5px'
    }
  }
}));

/**
 * Displays a list of associated devices in a single full-width grid row using DLS Chip components.
 * This component is commonly used in the Device Details view to render associated Sentinel or LMU
 * devices and their tags.
 */
export const AssociatedDevicesGridItem = memo(({
  labels: {
    associatedDevicesTitle,
    tags: tagsLabel
  },
  associatedTitle,
  associatedLabel,
  associatedValue,
  tags
}: AssociatedDevicesGridItemProps) => {
  const classes  = useStyles();
  const safeTags = Array.isArray(tags) ? tags : [];

  return (
    <Grid className={classes.formItem}
      xs={12}
      sm={12}
      md={12}
      lg={12}
      xl={12}
      item>
      <div className={classes.multiItemBody}>
        <span>{associatedTitle}</span>
        {associatedValue && (
          <span className={classes.tagChipContainer}>
            <Chip colorVariant={ChipVariant.GREEN}
              borderRadius="4px"
              labelColor={dlsColor('grey2')}
              label={`${associatedLabel}: ${associatedValue}`} />
          </span>
        )}
      </div>
      <div className={classes.multiItemBody}>
        <span>{`${tagsLabel}: `}</span>
        <span className={classes.tagChipContainer}>
          {safeTags.map((tag, index) => (
            <Chip key={`device-tag-${index}`}
              borderRadius="4px"
              labelColor={dlsColor('grey2')}
              label={tag} />
          ))}
        </span>
      </div>
    </Grid>
  );
});

export default AssociatedDevicesGridItem;
